import { twMerge } from '@florencelabs/fabric-styles';
import { MaterialSymbol } from '@florencelabs/icons';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { ReactElement, PropsWithChildren, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@provider-directory/components/analytics/analytics-provider';
import { PhoneNumber } from '@provider-directory/components/phone-number';
import { ProviderImage } from '@provider-directory/components/provider';
import { useAppointment } from '@provider-directory/contexts/AppointmentContext';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { createCalendarEvent } from '@provider-directory/functions/calendar';
import { getAvailablePhoneNumber } from '@provider-directory/functions/get-available-phone';
import {
  getMapsLinkByName,
  getLocationDisplayInfo,
} from '@provider-directory/functions/location';
import { AppPageName } from '@provider-directory/lib/analytics/conversions';
import { AddToCalendarModal } from '../add-to-calendar-modal/add-to-calendar-modal.component';
import { useOrgContext } from '../org-context/org-context-provider';
import { AppointmentSummaryActions } from './appointment-summary-actions';
import { AppointmentSummaryDetails } from './appointment-summary-details';
import {
  EVENT_DURATION,
  createAppointmentDateLabel,
  getAppointmentStatusByPath,
  printAppointmentDetails,
} from './appointment-summary.model';

const APPOINTMENT_TIME_ZONE = 'EDT';

const SCHEDULING_PAGE_MAP: Record<string, AppPageName> = {
  'select-time': 'SchedulingSelectTime',
  'patient-info': 'SchedulingPatientInfo',
  insurance: 'SchedulingInsurance',
  review: 'SchedulingReview',
  confirm: 'SchedulingConfirm',
};

export const AppointmentSummary = ({
  children,
}: PropsWithChildren): ReactElement => {
  const orgContext = useOrgContext();
  const { config } = orgContext;
  const { appointment } = useAppointment();
  const { provider } = useProvider();
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const pathname = usePathname();
  const [isAddToCalendarModalOpen, setIsAddToCalendarModalOpen] =
    useState(false);

  const appointmentStatus = getAppointmentStatusByPath(pathname);
  const isConfirmOrReviewState = ['confirm', 'review'].includes(
    appointmentStatus.id,
  );

  const selectedLocation = provider?.locations.find(
    (location) => location.ehrExternalId === appointment.departmentId,
  );

  const firstName = appointment.firstName || '';
  const middleName = appointment.middleName || '';
  const lastName = appointment.lastName || '';
  const patientName = [firstName, middleName, lastName]
    .filter(Boolean)
    .join(' ');

  const comments = appointment.comments || '';
  const insuranceCarrier = appointment.insuranceCarrier || '';
  const reasonForVisit = appointment.reasonForVisitKey || '';
  const isVideoVisitType = appointment.reasonForVisit === 'video';
  const appointmentDate = appointment.slotDate;
  const appointmentTime = appointment.slotTime;

  let locationDisplayName = '';
  let appointmentAddress = '';
  if (selectedLocation) {
    const displayInfo = getLocationDisplayInfo(selectedLocation);
    locationDisplayName = displayInfo.displayName;
    appointmentAddress = displayInfo.addressLabel;
  }
  const locationUrl = selectedLocation
    ? getMapsLinkByName(selectedLocation)
    : '';

  const locationPhone = selectedLocation?.contacts[0].value || '';

  const appointmentTypeLabel = t(
    `decisionTree.reasonForVisit.${reasonForVisit}.label`,
  );
  const durationInMinutes =
    appointment.duration || config.scheduling.eventDuration || EVENT_DURATION;
  const providerImage = provider?.urls.find((url) => url.type === 'photo');
  return (
    <div
      data-testid={'appointment-summary'}
      className={clsx(
        'flex items-start border-b border-black/20 text-gray-950 mobile:flex-col tablet:flex-row',
        'desktop:block desktop:flex-col desktop:border-none',
        isConfirmOrReviewState
          ? 'border-none tablet:flex-col'
          : 'tablet:gap-2 desktop:pl-[108px] desktop:pr-16',
      )}
    >
      {provider ? (
        <div
          data-testid={'appointment-provider-details'}
          className={clsx(
            'flex w-full items-center tablet:border-b-0',
            'desktop:w-auto',
            appointmentStatus.stepNumber > 0 || reasonForVisit
              ? 'border-b border-black/20 pb-6 tablet:pb-0 desktop:border-b'
              : null,
            isConfirmOrReviewState
              ? 'mb-4 rounded-lg border-none bg-white p-6 tablet:pb-6 desktop:pb-6'
              : 'mb-6 desktop:mb-8 desktop:pb-8',
          )}
        >
          <ProviderImage
            testIdPrefix={'appointment-provider'}
            name={provider.name}
            imageUrl={providerImage?.value}
            width={100}
            height={100}
            className={
              'mr-4 h-[70px] w-[70px] desktop:mr-6 desktop:h-[84px] desktop:w-[84px]'
            }
          />
          <div>
            <h2
              data-testid={'appointment-provider-name'}
              className={'text-body-large-bold'}
            >
              {provider.name.full}
            </h2>
            <p
              data-testid={'appointment-provider-specialty'}
              className={'text-body-small'}
            >
              {provider.specialties[0].name}
            </p>
          </div>
        </div>
      ) : null}

      <div
        data-testid={'appointment-visit-details'}
        className={twMerge(
          'w-full desktop:w-auto',
          isConfirmOrReviewState
            ? 'mb-16 rounded-lg border-none bg-white p-6 tablet:mb-24 print:mb-0'
            : 'flex flex-col tablet:pb-0 desktop:flex-col',
        )}
      >
        {appointmentStatus.stepNumber > 0 &&
          appointmentDate &&
          appointmentTime && (
            <div
              className={clsx(
                'flex items-start gap-4 desktop:font-serif',
                isConfirmOrReviewState
                  ? 'mb-6 flex-col gap-0'
                  : 'mb-3 gap-3 desktop:mb-6',
              )}
            >
              {!isConfirmOrReviewState && (
                <MaterialSymbol
                  name={'schedule_fill'}
                  size={20}
                  className={'shrink-0 desktop:hidden'}
                />
              )}
              <h3
                data-testid={'appointment-date-time'}
                className={clsx(
                  'text-body-small font-medium desktop:text-heading-2xlarge',
                  isConfirmOrReviewState && 'font-serif text-heading-2xlarge',
                )}
              >
                {createAppointmentDateLabel(
                  appointmentDate,
                  appointmentTime,
                  APPOINTMENT_TIME_ZONE,
                )}
              </h3>
            </div>
          )}
        {appointmentStatus.stepNumber > 0 && isVideoVisitType && (
          <AppointmentSummaryDetails
            title={t('schedule.videoVisitTypeLabel')}
            content={t('schedule.videoVisitTypeSchedulingLabel')}
            shouldHide={!isConfirmOrReviewState}
          />
        )}
        {appointmentStatus.stepNumber > 0 &&
          !isVideoVisitType &&
          selectedLocation && (
            <div
              data-testid={'appointment-location-details'}
              className={clsx(
                'flex desktop:flex-col desktop:items-start desktop:gap-0',
                isConfirmOrReviewState
                  ? 'mb-6 flex-col gap-0'
                  : 'mb-3 gap-3 desktop:mb-6',
              )}
            >
              <a
                href={locationUrl}
                target={'_blank'}
                rel={'noreferrer'}
                onClick={() => {
                  if (provider) {
                    analytics.directionsEvent({
                      provider,
                      phoneNumber: getAvailablePhoneNumber(
                        selectedLocation ?? null,
                        provider,
                        orgContext.getAssociationPhoneSelection(),
                      ),
                      linkContextPage:
                        SCHEDULING_PAGE_MAP[appointmentStatus.id] ??
                        'SchedulingPatientInfo',
                      clickText: locationDisplayName,
                      clickURL: locationUrl,
                    });
                  }
                }}
                aria-label={t('aria.getDirectionsTo', {
                  locationName: locationDisplayName,
                  address: appointmentAddress,
                })}
                className={clsx(
                  'flex items-center gap-1 leading-[140%] tracking-[0.36px] text-primary-color mobile:text-body-medium-semibold desktop:text-body-medium-semibold',
                  !isConfirmOrReviewState && 'hidden desktop:flex',
                )}
              >
                {locationDisplayName}{' '}
                <MaterialSymbol name={'north_east'} size={20} />
              </a>
              <div
                className={clsx(
                  'font-medium desktop:text-body-medium desktop:font-normal',
                  isConfirmOrReviewState
                    ? 'text-body-medium font-normal'
                    : 'text-body-small',
                )}
              >
                <div className={'flex flex-row gap-4'}>
                  {!isConfirmOrReviewState && (
                    <MaterialSymbol
                      name={'location_on_fill'}
                      size={20}
                      className={'shrink-0 desktop:hidden'}
                    />
                  )}
                  <div>
                    {appointmentAddress}
                    {locationPhone && (
                      <div
                        className={clsx(
                          'desktop:text-body-medium',
                          isConfirmOrReviewState
                            ? 'text-body-medium'
                            : 'text-body-small',
                        )}
                      >
                        <PhoneNumber phone={locationPhone} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        {appointmentStatus.stepNumber > 0 && appointmentTypeLabel && (
          <div
            data-testid={'appointment-type-details'}
            className={clsx(
              'flex desktop:flex-col desktop:items-start desktop:gap-0',
              isConfirmOrReviewState
                ? 'mb-6 flex-col gap-0'
                : 'mb-6 gap-3 desktop:mb-6',
            )}
          >
            <h3
              className={clsx(
                'text-body-medium-semibold',
                !isConfirmOrReviewState && 'hidden desktop:flex',
              )}
            >
              {t('schedule.reasonForVisit')}
            </h3>
            <div
              className={clsx(
                'font-medium desktop:text-body-medium desktop:font-normal',
                isConfirmOrReviewState
                  ? 'text-body-medium font-normal'
                  : 'text-body-small',
              )}
            >
              <div className={'flex flex-row gap-4'}>
                {!isConfirmOrReviewState && (
                  <MaterialSymbol
                    name={'event_note_fill'}
                    size={20}
                    className={'shrink-0 desktop:hidden'}
                  />
                )}
                <div>{appointmentTypeLabel}</div>
              </div>
            </div>
          </div>
        )}
        {appointmentStatus.stepNumber > 1 && patientName.trim() && (
          <AppointmentSummaryDetails
            title={t('schedule.patientName')}
            content={patientName}
            shouldHide={!isConfirmOrReviewState}
          />
        )}
        {appointmentStatus.stepNumber > 1 && comments && (
          <AppointmentSummaryDetails
            title={t('schedule.comments.title')}
            className={'patient-comments'}
            content={comments}
            shouldHide={!isConfirmOrReviewState}
            testKey={'comments'}
          />
        )}
        {appointmentStatus.stepNumber > 2 && insuranceCarrier && (
          <AppointmentSummaryDetails
            title={t('schedule.insurance')}
            content={insuranceCarrier}
            shouldHide={!isConfirmOrReviewState}
          />
        )}
        {appointmentStatus.id === 'confirm' && (
          <>
            <AppointmentSummaryActions
              action1={{
                text: t('schedule.printAppointmentDetails'),
                onClick: printAppointmentDetails,
                isHiddenOnMobile: true,
                className: 'hidden desktop:block text-button-medium',
              }}
              action2={{
                text: t('schedule.addToCalendar'),
                icon: 'calendar_today',
                onClick: () => {
                  setIsAddToCalendarModalOpen(true);
                },
                className: 'text-button-medium',
              }}
            />
            <AddToCalendarModal
              isOpen={isAddToCalendarModalOpen}
              setIsOpen={setIsAddToCalendarModalOpen}
              event={createCalendarEvent({
                title: t('schedule.calendars.eventTitle', {
                  appointmentType: appointmentTypeLabel,
                  providerName: provider?.name.full,
                }),
                appointmentDate,
                appointmentTime,
                appointmentAddress,
                locationUrl,
                durationMinutes: durationInMinutes,
                description: appointment.patientInstructions.join(', '),
              })}
            />
          </>
        )}

        {children}
      </div>
    </div>
  );
};
